import { supabase } from './supabase';
import emailService from './emailService';
import notificationService from './notificationService';

class ContactService {
  // Create a new contact
  async createContact(contactData) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data, error } = await supabase.rpc('create_contact', {
        contact_user_id: user?.id || null,
        contact_issue_type: contactData.issueType,
        contact_subject: contactData.subject,
        contact_message: contactData.message,
        contact_source_page: contactData.sourcePage || null,
        contact_source_url: contactData.sourceUrl || null,
        contact_metadata: contactData.metadata || {},
        contact_email_notifications: contactData.emailNotifications !== undefined ? contactData.emailNotifications : true
      });

      if (error) {
        console.error('Error creating contact:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: { id: data } };
    } catch (error) {
      console.error('Error in createContact:', error);
      return { success: false, error: error.message };
    }
  }

  // Get user's own contacts
  async getUserContacts(limit = 20, offset = 0) {
    try {
      const { data, error } = await supabase
        .from('contacts')
        .select('*')
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) {
        console.error('Error fetching user contacts:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error in getUserContacts:', error);
      return { success: false, error: error.message };
    }
  }

  // Get all contacts (admin only)
  async getAllContacts(filters = {}, limit = 50, offset = 0) {
    try {
      let query = supabase
        .from('contacts')
        .select(`
          *,
          user_profiles!contacts_user_id_fkey(id, full_name, email),
          assigned_admin:user_profiles!contacts_assigned_to_fkey(id, full_name, email)
        `);

      // Apply filters
      if (filters.status) {
        query = query.eq('status', filters.status);
      }
      if (filters.priority) {
        query = query.eq('priority', filters.priority);
      }
      if (filters.issueType) {
        query = query.eq('issue_type', filters.issueType);
      }
      if (filters.assignedTo) {
        query = query.eq('assigned_to', filters.assignedTo);
      }
      if (filters.search) {
        query = query.or(`subject.ilike.%${filters.search}%,message.ilike.%${filters.search}%`);
      }

      const { data, error } = await query
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) {
        console.error('Error fetching all contacts:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error in getAllContacts:', error);
      return { success: false, error: error.message };
    }
  }

  // Get contact by ID
  async getContactById(contactId) {
    try {
      const { data, error } = await supabase
        .from('contacts')
        .select(`
          *,
          user_profiles!contacts_user_id_fkey(id, full_name, email),
          assigned_admin:user_profiles!contacts_assigned_to_fkey(id, full_name, email)
        `)
        .eq('id', contactId)
        .single();

      if (error) {
        console.error('Error fetching contact:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in getContactById:', error);
      return { success: false, error: error.message };
    }
  }

  // Update contact status (admin only)
  async updateContactStatus(contactId, status, adminNote = null) {
    try {
      const { data, error } = await supabase.rpc('update_contact_status', {
        contact_id: contactId,
        new_status: status,
        admin_note: adminNote
      });

      if (error) {
        console.error('Error updating contact status:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in updateContactStatus:', error);
      return { success: false, error: error.message };
    }
  }

  // Assign contact to admin (admin only)
  async assignContact(contactId, adminUserId) {
    try {
      const { data, error } = await supabase.rpc('assign_contact', {
        contact_id: contactId,
        admin_user_id: adminUserId
      });

      if (error) {
        console.error('Error assigning contact:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in assignContact:', error);
      return { success: false, error: error.message };
    }
  }

  // Get contact statistics (admin only)
  async getContactStats() {
    try {
      const { data, error } = await supabase.rpc('get_contact_stats');

      if (error) {
        console.error('Error fetching contact stats:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data[0] || {} };
    } catch (error) {
      console.error('Error in getContactStats:', error);
      return { success: false, error: error.message };
    }
  }

  // Get admin users for assignment
  async getAdminUsers() {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, full_name, email')
        .eq('role', 'admin')
        .order('full_name');

      if (error) {
        console.error('Error fetching admin users:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error in getAdminUsers:', error);
      return { success: false, error: error.message };
    }
  }

  // Subscribe to contact updates (admin only)
  subscribeToContacts(callback) {
    const subscription = supabase
      .channel('contacts')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'contacts'
        },
        (payload) => {
          console.log('Contact update:', payload);
          callback(payload);
        }
      )
      .subscribe();

    return subscription;
  }

  // Unsubscribe from contact updates
  unsubscribeFromContacts(subscription) {
    if (subscription) {
      supabase.removeChannel(subscription);
    }
  }

  // Helper functions for UI
  getIssueTypeLabel(issueType) {
    const labels = {
      'technical_issue': 'Technical Issue',
      'billing_subscription': 'Billing & Subscription',
      'content_report': 'Content Report',
      'account_support': 'Account Support',
      'general_inquiry': 'General Inquiry',
      'community_violation': 'Community Violation',
      'privacy_request': 'Privacy Request',
      'security_issue': 'Security Issue',
      'legal_matter': 'Legal Matter',
      'game_bug': 'Game Bug',
      'feature_request': 'Feature Request',
      'partnership_inquiry': 'Partnership Inquiry',
      'developer_waitlist': 'Developer Waitlist'
    };
    return labels[issueType] || issueType;
  }

  getStatusLabel(status) {
    const labels = {
      'open': 'Open',
      'in_progress': 'In Progress',
      'resolved': 'Resolved',
      'closed': 'Closed'
    };
    return labels[status] || status;
  }

  getPriorityLabel(priority) {
    const labels = {
      'low': 'Low',
      'normal': 'Normal',
      'high': 'High',
      'urgent': 'Urgent'
    };
    return labels[priority] || priority;
  }

  getStatusColor(status) {
    const colors = {
      'open': 'text-red-500',
      'in_progress': 'text-yellow-500',
      'resolved': 'text-green-500',
      'closed': 'text-gray-500'
    };
    return colors[status] || 'text-gray-500';
  }

  getPriorityColor(priority) {
    const colors = {
      'low': 'text-gray-500',
      'normal': 'text-blue-500',
      'high': 'text-orange-500',
      'urgent': 'text-red-500'
    };
    return colors[priority] || 'text-gray-500';
  }

  // Format time for display
  formatContactTime(timestamp) {
    const now = new Date();
    const contactTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now - contactTime) / (1000 * 60));

    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours}h ago`;
    } else {
      const days = Math.floor(diffInMinutes / 1440);
      if (days === 1) {
        return 'Yesterday';
      } else if (days < 7) {
        return `${days}d ago`;
      } else {
        return contactTime.toLocaleDateString();
      }
    }
  }

  // Get issue type options for forms
  getIssueTypeOptions() {
    return [
      { value: 'technical_issue', label: 'Technical Issue' },
      { value: 'billing_subscription', label: 'Billing & Subscription' },
      { value: 'content_report', label: 'Content Report' },
      { value: 'account_support', label: 'Account Support' },
      { value: 'general_inquiry', label: 'General Inquiry' },
      { value: 'community_violation', label: 'Community Violation' },
      { value: 'privacy_request', label: 'Privacy Request' },
      { value: 'security_issue', label: 'Security Issue' },
      { value: 'legal_matter', label: 'Legal Matter' },
      { value: 'game_bug', label: 'Game Bug' },
      { value: 'feature_request', label: 'Feature Request' },
      { value: 'partnership_inquiry', label: 'Partnership Inquiry' },
      { value: 'developer_waitlist', label: 'Developer Waitlist' }
    ];
  }

  // Get contextual issue types based on source page
  getContextualIssueTypes(sourcePage) {
    const contextualTypes = {
      'Game Detail': ['game_bug', 'content_report', 'technical_issue'],
      'Subscription Management': ['billing_subscription', 'account_support'],
      'Privacy Policy': ['privacy_request', 'legal_matter'],
      'Terms of Use': ['legal_matter', 'general_inquiry'],
      'Community Guidelines': ['community_violation', 'content_report'],
      'User Profile': ['account_support', 'privacy_request'],
      'Admin Dashboard': ['technical_issue', 'feature_request'],
      'Developers': ['developer_waitlist', 'partnership_inquiry', 'general_inquiry']
    };

    const suggested = contextualTypes[sourcePage] || [];
    const allTypes = this.getIssueTypeOptions();
    
    // Put suggested types first, then others
    const suggestedOptions = allTypes.filter(type => suggested.includes(type.value));
    const otherOptions = allTypes.filter(type => !suggested.includes(type.value));
    
    return [...suggestedOptions, ...otherOptions];
  }

  // Send admin reply email (admin only)
  async sendAdminReply(contactId, adminMessage) {
    try {
      // Get current admin user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('No authenticated user found');
      }

      const { data: adminProfile } = await supabase
        .from('user_profiles')
        .select('id, full_name, email, role')
        .eq('id', user.id)
        .single();

      if (!adminProfile || adminProfile.role !== 'admin') {
        throw new Error('Only admins can send replies');
      }

      // Get contact details
      const contactResult = await this.getContactById(contactId);
      if (!contactResult.success) {
        throw new Error('Contact not found');
      }

      const contact = contactResult.data;
      if (!contact.user_profiles?.email) {
        throw new Error('Contact has no associated user email');
      }

      // Send email
      const emailResult = await emailService.sendContactAdminReply(
        contact,
        adminMessage,
        adminProfile
      );

      if (!emailResult.success) {
        console.error('Failed to send admin reply email:', {
          contactId,
          error: emailResult.error,
          timestamp: new Date().toISOString(),
          adminId: adminProfile.id,
          messagePreview: adminMessage.substring(0, 100)
        });
        // TODO: Store in failed_email_queue table for retry (see backlog)
        return { success: false, error: 'Failed to send email: ' + emailResult.error };
      }

      // Create notification for the user
      if (contact.user_id) {
        await notificationService.createNotification(
          contact.user_id,
          'contact_update',
          'Response to your support ticket',
          `${adminProfile.full_name || 'Support Team'} responded: "${adminMessage.substring(0, 100)}${adminMessage.length > 100 ? '...' : ''}"`,
          `/user-profile-settings?tab=support&ticket=${contactId}`,
          {
            contactId: contactId,
            adminName: adminProfile.full_name || 'Support Team'
          }
        );
      }

      // Update contact status to in_progress if it was open
      if (contact.status === 'open') {
        await this.updateContactStatus(contactId, 'in_progress');
      }

      return { success: true, data: emailResult.data };
    } catch (error) {
      console.error('Error in sendAdminReply:', error);
      return { success: false, error: error.message };
    }
  }

  // Send status update email (admin only)
  async sendStatusUpdateEmail(contactId, adminNote = null) {
    try {
      // Get contact details
      const contactResult = await this.getContactById(contactId);
      if (!contactResult.success) {
        throw new Error('Contact not found');
      }

      const contact = contactResult.data;
      if (!contact.user_profiles?.email) {
        console.log('Contact has no associated user email, skipping status update email');
        return { success: true, data: { skipped: true, reason: 'No user email' } };
      }

      // Send email
      const emailResult = await emailService.sendContactStatusUpdate(contact, adminNote);

      if (!emailResult.success) {
        console.error('Failed to send status update email:', emailResult.error);
        return { success: false, error: 'Failed to send email: ' + emailResult.error };
      }

      // Create notification for the user
      if (contact.user_id) {
        const statusDisplayMap = {
          'open': 'Open',
          'in_progress': 'In Progress',
          'resolved': 'Resolved',
          'closed': 'Closed'
        };
        const statusDisplay = statusDisplayMap[contact.status] || contact.status;

        await notificationService.createNotification(
          contact.user_id,
          'contact_update',
          `Support ticket ${statusDisplay.toLowerCase()}`,
          `Your ticket "${contact.subject}" status was updated to ${statusDisplay}${adminNote ? `: ${adminNote}` : ''}`,
          `/user-profile-settings?tab=support&ticket=${contact.id}`,
          {
            contactId: contact.id,
            status: contact.status,
            adminNote: adminNote
          }
        );
      }

      return { success: true, data: emailResult.data };
    } catch (error) {
      console.error('Error in sendStatusUpdateEmail:', error);
      return { success: false, error: error.message };
    }
  }

  // Update contact status with email notification (admin only)
  async updateContactStatusWithEmail(contactId, status, adminNote = null) {
    try {
      // Update status in database
      const updateResult = await this.updateContactStatus(contactId, status, adminNote);
      if (!updateResult.success) {
        return updateResult;
      }

      // Send email notification
      const emailResult = await this.sendStatusUpdateEmail(contactId, adminNote);
      if (!emailResult.success) {
        console.error('Status updated but email failed:', {
          contactId,
          error: emailResult.error,
          timestamp: new Date().toISOString(),
          status: status,
          adminNote: adminNote
        });
        // TODO: Store in failed_email_queue table for retry (see backlog)
        // Don't fail the whole operation if email fails
      }

      return { 
        success: true, 
        data: {
          statusUpdate: updateResult.data,
          emailSent: emailResult.success,
          emailError: emailResult.success ? null : emailResult.error
        }
      };
    } catch (error) {
      console.error('Error in updateContactStatusWithEmail:', error);
      return { success: false, error: error.message };
    }
  }

  // Get email threads for a contact (admin only)
  async getContactEmailThreads(contactId) {
    try {
      const { data, error } = await supabase
        .from('email_threads')
        .select('*')
        .eq('contact_id', contactId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching email threads:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error in getContactEmailThreads:', error);
      return { success: false, error: error.message };
    }
  }

  // Get email logs for a contact (admin only)
  async getContactEmailLogs(contactId) {
    try {
      const { data, error } = await supabase
        .from('email_logs')
        .select('*')
        .eq('contact_id', contactId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching contact email logs:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error in getContactEmailLogs:', error);
      return { success: false, error: error.message };
    }
  }
}

const contactService = new ContactService();
export default contactService;
